<template>
  <div class="d-flex rtls-text-field-container">
    <v-text-field
      v-bind="$attrs"
      :key="fieldKey"
      v-model="inputValue"
      :disabled="disabled"
      :rules="rules"
      :hide-details="hideDetails"
      :variant="variant"
      :bg-color="bgColor"
      :style="widthStyle"
      :placeholder="placeholder"
      :validate-on="validateOn"
      @update:model-value="handleInput"
      @blur="handleBlur"
      @keyup.enter="$emit('keyup.enter', $event)"
      @keyup.esc="$emit('keyup.esc', $event)"
    />
    <!-- use $attrs to pass all event listeners on rtls-text-field through to v-text-field -->
    <ErrorTriangle
      :class="errorTriangleClass"
      :errors="errors"
      :server-error="serverError"
      :small="small"
      :tooltip-location="tooltipLocation"
    />
  </div>
</template>

<script>
import { every, isEmpty, forEach } from 'lodash';

export default {
  name: 'AssortmentTextField',
  inheritAttrs: false,
  props: {
    // ---  error-triangle props ---
    small: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    // --- text-field props ---
    fieldKey: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    modelValue: {
      required: true,
      type: [String, Number, null],
    },
    disabled: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    // set inline style width of v-text-field. see widthStyle computed.
    width: {
      required: false,
      type: String,
      default: '100%',
    },
    bgColor: {
      required: false,
      type: String,
      default: 'transparent',
    },
    hideDetails: {
      required: false,
      type: Boolean,
      default: true,
    },
    placeholder: {
      required: false,
      type: String,
      default: '',
    },
    variant: {
      required: false,
      type: String,
      default: 'solo',
    },
    tooltipLocation: {
      required: false,
      type: String,
      default: 'end',
    },
    validateOn: {
      required: false,
      type: String,
      default: '',
    },
    // --- shared props ---
    // array of functions to be evaluated in updateAndValidate method.
    rules: {
      required: false,
      type: Array,
      default: () => [],
    },
    serverError: {
      required: false,
      type: String,
      default: () => '',
    },
  },

  emits: ['update:modelValue', 'change', 'blur', 'valid', 'invalid', 'keyup.enter', 'keyup.esc'],

  data() {
    return {
      inputValue: this.modelValue,
      errors: {},
    };
  },

  computed: {
    errorTriangleClass() {
      if (this.small) return '';

      return 'ml-1 mr-1';
    },

    error() {
      return !every(this.errors) || !isEmpty(this.serverError);
    },

    widthStyle() {
      return `width: ${this.width}; max-width: ${this.width}`;
    },
  },

  watch: {
    modelValue(newValue) {
      this.inputValue = newValue || '';
    },
  },

  mounted() {
    this.updateAndValidate(this.inputValue);
  },

  methods: {
    handleInput(value) {
      this.updateAndValidate(value.trim());
      this.$emit('update:modelValue', value);
    },

    handleBlur(event) {
      // Only trimming the emitted value on blur allows spaces to be entered while still editing the value
      // but ensures saved values will still be trimmed.
      if (typeof this.inputValue === 'string') {
        this.updateAndValidate(this.inputValue.trim());
        this.$emit('update:modelValue', this.inputValue.trim());
      }
      this.$emit('blur', event);
    },

    updateAndValidate(value) {
      const errs = {};
      // need lodash forEach to exit early with return false
      // https://lodash.com/docs/4.17.15#forEach "Iteratee functions may exit iteration early by explicitly returning false"
      forEach(this.rules, rule => {
        const valid = rule(value);
        if (valid !== true) {
          errs[valid] = false;
          return false;
        }
      });

      this.errors = errs;

      if (isEmpty(errs)) return this.$emit('valid');
      return this.$emit('invalid');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.col,
.row {
  max-height: 28px;
}

.rtls-text-field-container {
  width: 100%;
}
</style>
